import React, { useState, useRef, useEffect } from 'react';
import '../styles/Contact.css';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import SEO from '../components/SEO';

// Define types for contact form data
interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  preferredContact: string;
  acceptTerms: boolean;
  subscribeEmail: boolean;  // New field for email subscription
}

// Define contact method type
type ContactMethod = {
  id: string;
  label: string;
  icon: string;
  info: string;
  link?: string;
};

// Define FAQ type
type FAQ = {
  question: string;
  answer: string;
};

// EmailJS configuration
const EMAILJS_SERVICE_ID = "service_l09crg6";
const EMAILJS_TEMPLATE_ID = "template_px37aww";
const EMAILJS_AUTO_REPLY_TEMPLATE_ID = "template_kixr55c";
const EMAILJS_PUBLIC_KEY = "WSVr9WuU7rI-NrhiM";

const Contact: React.FC = () => {
  // Form state
  const [contactData, setContactData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    preferredContact: 'email',
    acceptTerms: false,
    subscribeEmail: false,  // Initialize new field
  });

  // UI states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const formRef = useRef<HTMLFormElement>(null);

  // Contact page specific schemas
  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://vitalbalancehormones.com/contact"
    },
    "headline": "Contact VitalBalanceHormones | Get in Touch With Our Hormone Specialists",
    "description": "Contact our hormone therapy specialists today. Schedule a consultation, ask questions about our treatments, or get directions to our Littleton, CO clinic."
  };
  
  // Local business schema for enhanced local SEO
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": "VitalBalanceHormones",
    "image": "https://vitalbalancehormones.com/logo512.png",
    "url": "https://vitalbalancehormones.com",
    "telephone": "+17207735733",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "8906 W Bowles Ave, Suite 110",
      "addressLocality": "Littleton",
      "addressRegion": "CO",
      "postalCode": "80123",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 39.6111801,
      "longitude": -105.0997231
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Wednesday", "Thursday", "Friday"],
        "opens": "10:00",
        "closes": "19:00"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/vitalbalancehormones",
      "https://www.instagram.com/vitalbalancehormones",
      "https://www.linkedin.com/company/vitalbalancehormones"
    ]
  };

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init(EMAILJS_PUBLIC_KEY);
  }, []);

  // Contact methods
  const contactMethods: ContactMethod[] = [
    {
      id: 'phone',
      label: 'Call Us',
      icon: 'phone',
      info: '720-773-5733',
      link: 'tel:+17207735733'
    },
    {
      id: 'email',
      label: 'Email Us',
      icon: 'envelope',
      info: 'info@vitalbalancehormones.com',
      link: 'mailto:info@vitalbalancehormones.com'
    },
    {
      id: 'office',
      label: 'Visit Us',
      icon: 'location-dot',
      info: '8906 W Bowles Ave, Suite 110, Littleton, CO 80123'
    },
    {
      id: 'hours',
      label: 'Office Hours',
      icon: 'clock',
      info: 'Mon, Wed, Thurs, Fri: 10AM – 7PM'
    }
  ];

  // FAQ items
  const faqItems: FAQ[] = [
    {
      question: "What should I expect during my first appointment?",
      answer: "Your first appointment will include a comprehensive review of your health history, current symptoms, and wellness goals. We'll discuss potential treatment options and may recommend lab testing to assess your hormonal profile. Our goal is to understand your unique situation and begin creating a personalized plan for your care."
    },
    {
      question: "How long does it take to see results from bioidentical hormone therapy?",
      answer: "Many patients report improvement in some symptoms within 2-3 weeks of beginning treatment. However, the full benefits typically develop over 3-6 months as your body adjusts to hormonal optimization. We'll closely monitor your progress and make adjustments to your treatment plan as needed."
    },
    {
      question: "Are bioidentical hormones covered by insurance?",
      answer: "Coverage varies by insurance provider and plan. While some insurance plans cover portions of bioidentical hormone therapy, others may not provide coverage. We recommend contacting your insurance company directly to inquire about your specific coverage. Our staff can provide you with the necessary information to submit to your insurance."
    },
    {
      question: "How do I know if I need hormone therapy?",
      answer: "Common indicators include fatigue, weight changes, mood swings, sleep disturbances, hot flashes, night sweats, decreased libido, and brain fog. The only way to definitively determine if hormone therapy is right for you is through proper evaluation and testing. We offer comprehensive hormone assessments to identify imbalances and determine if treatment would benefit you."
    }
  ];

  // Input change handler
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    
    // Handle checkbox inputs differently
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setContactData({ ...contactData, [name]: checked });
    } else {
      setContactData({ ...contactData, [name]: value });
    }
  };

  // Form submission handler with EmailJS
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset status and set submitting state
    setSubmitStatus('idle');
    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      // Format date and time for admin notification
      const now = new Date();
      const formattedDate = now.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      // Prepare template parameters for admin notification
      const adminTemplateParams = {
        name: `${contactData.firstName} ${contactData.lastName}`,
        email: contactData.email,
        phone: contactData.phone || 'Not provided',
        subject: contactData.subject,
        message: contactData.message,
        preferredContact: contactData.preferredContact,
        time: formattedDate
      };
      
      // Prepare template parameters for auto-reply
      const autoReplyParams = {
        to_name: `${contactData.firstName} ${contactData.lastName}`,
        to_email: contactData.email,
        subject: contactData.subject
      };
      
      // Send admin notification - ignore 422 errors since they're not preventing email delivery
      try {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          adminTemplateParams
        );
      } catch (error) {
        console.log("Admin notification attempted, continuing with auto-reply");
      }
      
      // Send auto-reply to user - ignore 422 errors
      try {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_AUTO_REPLY_TEMPLATE_ID,
          autoReplyParams
        );
      } catch (error) {
        console.log("Auto-reply attempted, continuing to success state");
      }
      
      // Display success state regardless of 422 errors
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setTimeout(() => {
        setContactData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          preferredContact: 'email',
          acceptTerms: false,
          subscribeEmail: false,  // Reset new field
        });
      }, 5000);
    } catch (error) {
      // Only report errors that aren't 422 (service validation errors)
      if (error instanceof Error && error.toString().includes('422')) {
        // Emails are still being sent despite 422 errors, so show success
        setSubmitStatus('success');
        
        setTimeout(() => {
          setContactData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            subject: '',
            message: '',
            preferredContact: 'email',
            acceptTerms: false,
            subscribeEmail: false,  // Reset new field
          });
        }, 5000);
      } else {
        // Handle other errors
        console.error('Email send error:', error);
        setSubmitStatus('error');
        setErrorMessage(error instanceof Error ? error.message : 'Failed to send message. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle FAQ accordions
  const toggleAccordion = (index: number) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  // Scroll to form
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="contact-page">
      <SEO 
        title="Contact Us | VitalBalanceHormones Hormone Therapy Specialists"
        description="Contact our hormone therapy specialists today. Schedule a consultation, ask questions, or get directions to our Littleton clinic. We're here to help you start your wellness journey."
        keywords="hormone therapy contact, bioidentical hormone consultation, BHRT appointment, hormone specialist contact, women's hormone therapy Littleton"
        url="/contact"
        schema={[contactSchema, localBusinessSchema]}
      />
      
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-content">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Get in Touch
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.7 }}
          >
            We're here to answer your questions and guide you on your wellness journey
          </motion.p>
          <motion.button
            className="hero-cta-button"
            onClick={scrollToForm}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Contact Us Now
          </motion.button>
        </div>
      </section>

      <div className="contact-content-wrapper">
        {/* Left Column - Form */}
        <div className="contact-main-column">
          {/* Contact Form Section */}
          <section className="contact-form-section" ref={formRef}>
            <div className="contact-container">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <h2>Send Us a Message</h2>
                <p className="form-description">
                  Have questions about our treatments or ready to start your wellness journey? Fill out the form below and we'll get back to you as soon as possible.
                </p>

                {submitStatus === 'success' ? (
                  <div className="success-message">
                    <div className="success-icon">
                      <i className="icon-check-circle"></i>
                    </div>
                    <h3>Thank You!</h3>
                    <p>Your message has been sent successfully. We've also sent you a confirmation email. We'll respond to you shortly.</p>
                  </div>
                ) : submitStatus === 'error' ? (
                  <div className="error-message">
                    <div className="error-icon">
                      <i className="icon-exclamation-circle"></i>
                    </div>
                    <h3>Oops!</h3>
                    <p>{errorMessage || 'Something went wrong while sending your message. Please try again or contact us directly.'}</p>
                    <button 
                      onClick={() => setSubmitStatus('idle')}
                      className="try-again-button"
                    >
                      Try Again
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="contact-form" ref={formRef}>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="firstName" className="contact-label">
                          First Name <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={contactData.firstName}
                          onChange={handleChange}
                          className="contact-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="lastName" className="contact-label">
                          Last Name <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={contactData.lastName}
                          onChange={handleChange}
                          className="contact-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="email" className="contact-label">
                          Email <span className="required">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={contactData.email}
                          onChange={handleChange}
                          className="contact-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="phone" className="contact-label">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={contactData.phone}
                          onChange={handleChange}
                          className="contact-input"
                          placeholder="(000) 000-0000"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="subject" className="contact-label">
                        Subject <span className="required">*</span>
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={contactData.subject}
                        onChange={handleChange}
                        className="contact-select"
                        required
                      >
                        <option value="">Please select a subject</option>
                        <option value="Schedule an Appointment">Schedule an Appointment</option>
                        <option value="Treatment Information">Treatment Information</option>
                        <option value="Pricing and Insurance">Pricing and Insurance</option>
                        <option value="Feedback">Feedback</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message" className="contact-label">
                        Message <span className="required">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={contactData.message}
                        onChange={handleChange}
                        className="contact-textarea"
                        rows={5}
                        placeholder="How can we help you today?"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="contact-label">Preferred Contact Method</label>
                      <div className="radio-group">
                        <div className="radio-option">
                          <input
                            type="radio"
                            id="contactEmail"
                            name="preferredContact"
                            value="email"
                            checked={contactData.preferredContact === 'email'}
                            onChange={handleChange}
                          />
                          <label htmlFor="contactEmail">Email</label>
                        </div>
                        <div className="radio-option">
                          <input
                            type="radio"
                            id="contactPhone"
                            name="preferredContact"
                            value="phone"
                            checked={contactData.preferredContact === 'phone'}
                            onChange={handleChange}
                          />
                          <label htmlFor="contactPhone">Phone Call</label>
                        </div>
                        <div className="radio-option">
                          <input
                            type="radio"
                            id="contactText"
                            name="preferredContact"
                            value="text"
                            checked={contactData.preferredContact === 'text'}
                            onChange={handleChange}
                          />
                          <label htmlFor="contactText">Text Message</label>
                        </div>
                      </div>
                    </div>

                    <div className="form-group checkbox-group">
                      <input
                        type="checkbox"
                        id="subscribeEmail"
                        name="subscribeEmail"
                        checked={contactData.subscribeEmail}
                        onChange={handleChange}
                      />
                      <label htmlFor="subscribeEmail">
                        Subscribe to our newsletter for updates on hormone health and wellness
                      </label>
                    </div>

                    <div className="form-group checkbox-group">
                      <input
                        type="checkbox"
                        id="acceptTerms"
                        name="acceptTerms"
                        checked={contactData.acceptTerms}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor="acceptTerms">
                        I agree to the <a href="/privacy-policy">Privacy Policy</a> and consent to being contacted regarding my inquiry.
                      </label>
                    </div>

                    <button
                      type="submit"
                      className="contact-button"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="button-content">
                          <span className="spinner"></span> Sending...
                        </span>
                      ) : (
                        <span className="button-content">
                          Send Message <i className="icon-paper-plane"></i>
                        </span>
                      )}
                    </button>
                  </form>
                )}
              </motion.div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="faq-section">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="faq-container"
            >
              <h2>Frequently Asked Questions</h2>
              <div className="faq-list">
                {faqItems.map((faq, index) => (
                  <div 
                    key={index} 
                    className={`faq-item ${activeAccordion === index ? 'active' : ''}`}
                  >
                    <button 
                      className="faq-question" 
                      onClick={() => toggleAccordion(index)}
                      aria-expanded={activeAccordion === index}
                    >
                      {faq.question}
                      <span className="accordion-icon">
                        <i className={`icon-${activeAccordion === index ? 'minus' : 'plus'}`}></i>
                      </span>
                    </button>
                    <div className="faq-answer" aria-hidden={activeAccordion !== index}>
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </section>
        </div>

        {/* Right Column - Contact Info & Map */}
        <div className="contact-sidebar-column">
          {/* Contact Methods */}
          <section className="contact-methods-section">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="contact-sidebar-container"
            >
              <h2>Contact Information</h2>
              <div className="contact-methods-list">
                {contactMethods.map((method) => (
                  <motion.div 
                    key={method.id} 
                    className="contact-method-item"
                    variants={itemVariants}
                  >
                    <div className="method-icon">
                      <i className={`icon-${method.icon}`}></i>
                    </div>
                    <div className="method-content">
                      <h3>{method.label}</h3>
                      {method.link ? (
                        <a href={method.link} className="method-info">
                          {method.info}
                        </a>
                      ) : (
                        <p className="method-info">{method.info}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Map Section */}
          <section className="map-section">
            <div className="map-container">
              <div className="map-header">
                <h2>Our Location</h2>
                <p>Visit us at our wellness center</p>
              </div>
              <div className="map-wrapper">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3071.664099738537!2d-105.0997231!3d39.6111801!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x876b79eebe240469%3A0x45fcad09c81a6344!2s8906%20W%20Bowles%20Ave%20%23110%2C%20Littleton%2C%20CO%2080123!5e0!3m2!1sen!2sus!4v1717711771653!5m2!1sen!2sus" 
                  className="interactive-map"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                ></iframe>
              </div>
              <div className="map-footer">
                <div className="address-card">
                  <div className="address-icon">
                    <i className="icon-location-dot"></i>
                  </div>
                  <div className="address-content">
                    <p className="address-line">8906 W Bowles Ave, Suite 110</p>
                    <p className="address-line">Littleton, CO 80123</p>
                  </div>
                </div>
                <a 
                  href="https://www.google.com/maps/dir//8906+W+Bowles+Ave+%23110,+Littleton,+CO+80123/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="directions-button"
                >
                  Get Directions <i className="icon-directions"></i>
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Contact;